import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { LogOut, LayoutDashboard, Shield, CreditCard, UserCircle, MapPin, BookOpen } from "lucide-react";
import OnboardingChatbot from "@/components/onboarding/OnboardingChatbot";
import TrialLimitModal from "@/components/TrialLimitModal";

export const DashboardLayout = ({ children }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  const subStatus = user?.subscription?.status;
  const subBadge = subStatus === "active" ? "Attivo" : subStatus === "trial" ? "Prova" : "Scaduto";
  const subColor = subStatus === "active" ? "bg-green-100 text-green-800" : subStatus === "trial" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-8 h-8 bg-[#c8e414] rounded-full flex items-center justify-center">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/><path d="M12 3v12"/><path d="M4 19h16"/></svg>
            </div>
            <span className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">Tabellone Facile</span>
          </Link>

          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-sm text-slate-600 hover:text-green-700 transition-colors flex items-center gap-1.5" data-testid="nav-dashboard">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            {isSuperAdmin && (
              <Link to="/admin" className="text-sm text-slate-600 hover:text-green-700 transition-colors flex items-center gap-1.5" data-testid="nav-admin">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Admin</span>
              </Link>
            )}
            {!isSuperAdmin && (
              <Badge className={`rounded-sm text-[10px] ${subColor} hover:${subColor} cursor-pointer`} onClick={() => navigate("/subscription")}>
                {subBadge}
              </Badge>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="focus:outline-none">
                <Avatar className="h-9 w-9 border-2 border-slate-200 hover:border-green-600 transition-colors cursor-pointer">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-green-700 text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  {isSuperAdmin && <Badge className="mt-1 bg-purple-100 text-purple-800 hover:bg-purple-100 text-[10px] rounded-sm">Super Admin</Badge>}
                </div>
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                  <UserCircle className="mr-2 h-4 w-4" /> Il Mio Profilo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/venue")} className="cursor-pointer">
                  <MapPin className="mr-2 h-4 w-4" /> Info Circolo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/subscription")} className="cursor-pointer">
                  <CreditCard className="mr-2 h-4 w-4" /> Abbonamento
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/guida")} className="cursor-pointer" data-testid="nav-guida">
                  <BookOpen className="mr-2 h-4 w-4" /> Guida all'uso
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">{children}</main>
      <OnboardingChatbot />
      <TrialLimitModal />
    </div>
  );
};
